import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Button, InputSelect, Modal, Notification, ScrollView, styles, Text, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { getCaption } from './helpers';
import { L10N } from './ModalExtras.l10n';
import * as style from './ModalExtras.module.css';
import { getSafeTrust } from '../../../../trust';
import { EVENT, getGuestLabel, METRICS_EVENTS } from '../../../helpers';
import { getLabelTaxes } from '../../helpers';
import { PriceDetails } from '../Item/components';

const ModalExtras = ({
  busy,
  cart = [],
  extras: propExtras = [],
  taxesType,
  visible = false,
  onClose = () => {},
  onSubmit = () => {},
  ...others
}) => {
  const { currencyFormat, translate } = useLocale();
  const { value: store = {} } = useStore();
  const { currency, finder = {}, hotel: { accommodationType: hotelAccommodationType, variants = {} } = {} } = store;

  const [extras, setExtras] = useState(propExtras);

  useEffect(() => {
    if (visible) {
      setExtras(propExtras);
      const roomType = cart?.[0]?.roomName;
      const roomPrice = cart?.reduce((total, { price = 0 } = {}) => total + price, 0);
      Event.publish(EVENT.METRICS, {
        id: METRICS_EVENTS.EXTRAS_READY,
        room_type: roomType,
        room_price: roomPrice,
        extras_count: propExtras?.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propExtras, visible]);

  const handleChange = ({ id, field, value } = {}) => {
    getSafeTrust(store).record('journeyAction', { stage: 'rates', name: 'extrasChanged' });
    // ! Why parse Number()? should come from the service.
    setExtras(extras.map((extra) => ({ ...extra, [field]: extra.id === id ? parseInt(value) : extra[field] })));
  };

  const handleSelect = (id) => {
    getSafeTrust(store).record('journeyAction', { stage: 'rates', name: 'extrasChanged' });
    const next = extras.map(({ selected = false, ...extra }) => ({
      ...extra,
      selected: extra.id === id ? !selected : selected,
    }));

    setExtras(next);

    const selectedExtra = next.find((extra) => extra.id === id);
    const totalSelected = next.filter(({ selected }) => selected).length;
    const { amount, nights, value, valueAmount, valueNights } = selectedExtra || {};
    const price = value?.[valueAmount || amount]?.[valueNights || nights]?.price;
    const position = next.findIndex((extra) => extra.id === id) + 1;
    Event.publish(EVENT.METRICS, {
      id: METRICS_EVENTS.EXTRAS_INTERACTION,
      action: selectedExtra?.selected ? 'add' : 'remove',
      extra_name: selectedExtra?.title,
      extra_price: price,
      extra_position: position || undefined,
      total_extras_selected: totalSelected,
    });
  };

  const handleSubmit = () => {
    getSafeTrust(store).record('journeyAction', {
      stage: 'rates',
      name: extras.some(({ selected, included }) => selected && !included) ? 'extrasSubmitted' : 'extrasSkipped',
    });
    onSubmit(
      extras
        .filter((item) => item.selected || item.included)
        .map(({ amount, baseValue, nights, id, included, valueAmount, valueNights }) => ({
          id,
          included,
          amount: parseInt(valueAmount || amount),
          nights: parseInt(valueNights || nights),
          value: baseValue[valueAmount || amount][valueNights || nights]?.price,
        })),
    );
  };

  const hasSelected = extras.filter((item) => !!item.selected).length > 0;
  const total = cart.reduce((value, { price } = {}) => value + price, 0);
  const totalExtras = extras
    .filter((item) => item.selected)
    .reduce(
      (total, { amount, nights, valueAmount, valueNights, value = {} } = {}) =>
        total + (value[valueAmount || amount][valueNights || nights]?.price || 0),
      0,
    );
  const { place: { id: [id] = [] } = {} } = finder;
  const { accommodationType = hotelAccommodationType } = variants[id] || {};
  const standaloneCard = extras.length === 1;
  const currencyFormatProps = { currency, maximumFractionDigits: 0, minimumFractionDigits: 0 };
  const { testId } = others;

  return (
    <Modal
      fit
      title={translate(L10N.LABEL_ANYTHING_ELSE_TO_ADD)}
      visible={visible}
      onClose={onClose}
      className={style.modal}
      testId={testId}
    >
      <ScrollView scrollIndicator scrollTo={visible ? 0.5 : undefined} snap={false} className={style.scrollView}>
        <View row className={style.container}>
          {extras.map(
            ({
              accommodationThreshold = 0,
              amount = 0,
              conditions,
              frequency,
              guestThreshold = 0,
              guestType,
              id,
              image,
              included = false,
              inputGuests = false,
              inputNights = false,
              nights = 0,
              selected = false,
              text,
              title,
              type,
              value = {},
              valueAmount = amount,
              valueNights = nights,
            } = {}) => {
              const { price: total } = value[valueAmount][valueNights] || {};

              return (
                <View
                  key={id}
                  wide
                  className={styles(
                    style.card,
                    standaloneCard && style.standalone,
                    hasSelected && selected ? style.selected : undefined,
                  )}
                >
                  <View className={style.image} style={{ backgroundImage: `url(${image})` }} />

                  <View wide className={[style.gap, style.offset, style.anchor]}>
                    <Text bold>{title}</Text>
                    {text && <Text small>{text}</Text>}

                    {accommodationThreshold > 0 && (
                      <Notification inline small warning>
                        {translate(L10N.LABEL_APPLIES_SELECTED_ROOMS, { value: accommodationThreshold })}
                      </Notification>
                    )}

                    {guestThreshold > 0 && (
                      <Notification inline small warning>
                        {translate(L10N.LABEL_APPLIES_INDIVIDUALS_RESERVATION, { value: guestThreshold })}
                      </Notification>
                    )}

                    {conditions && (
                      <Text light small>
                        {conditions}
                      </Text>
                    )}

                    {(inputGuests || inputNights) && (
                      <View row className={styles(style.gap, style.inputs)}>
                        {inputGuests && (
                          <InputSelect
                            capitalize
                            name="guests"
                            label={getGuestLabel({ plural: true, type: guestType }, translate)}
                            options={Array.from({ length: amount }).map((_, index) => index + 1)}
                            value={valueAmount?.toString()}
                            onChange={(value) => handleChange({ id, field: 'valueAmount', value })}
                          />
                        )}
                        {inputNights && (
                          <InputSelect
                            name="nights"
                            label={translate(L10N.LABEL_NIGHTS)}
                            options={Array.from({ length: nights }).map((_, index) => index + 1)}
                            value={valueNights?.toString()}
                            onChange={(value) => handleChange({ id, field: 'valueNights', value })}
                          />
                        )}
                      </View>
                    )}
                  </View>

                  <View row className={[style.offset, style.spaceBetween]}>
                    <View>
                      <Text bold headline>
                        {total ? currencyFormat({ ...currencyFormatProps, value: total }) : translate(L10N.LABEL_FREE)}
                      </Text>
                      <Text light small>
                        {getCaption(
                          {
                            accommodationType,
                            amount,
                            frequency,
                            guestType,
                            inputGuests,
                            type,
                            valueAmount,
                            valueNights,
                          },
                          translate,
                        )}
                      </Text>
                    </View>

                    <Button
                      small
                      secondary={!selected && !included}
                      onPress={!included ? () => handleSelect(id) : undefined}
                    >
                      {translate(included ? L10N.LABEL_INCLUDED : selected ? L10N.ACTION_REMOVE : L10N.ACTION_ADD)}
                    </Button>
                  </View>
                </View>
              );
            },
          )}
        </View>
      </ScrollView>

      <View row className={style.footer}>
        {total > 0 && (
          <View className={style.total}>
            <Text bold headline level={2}>
              {currencyFormat({ ...currencyFormatProps, value: total + totalExtras })}
            </Text>

            {!!taxesType && (
              <Text light small>
                {translate(getLabelTaxes(taxesType))}
              </Text>
            )}

            <PriceDetails
              extras={extras.filter(({ selected }) => selected)}
              cart={cart}
              taxesType={taxesType}
              {...others}
              className={style.priceDetails}
            />
          </View>
        )}

        <Button busy={busy} onPress={handleSubmit} testId={testId ? `${testId}-submit` : undefined}>
          {translate(hasSelected ? L10N.ACTION_CONTINUE : L10N.ACTION_CONTINUE_WITHOUT_EXTRAS)}
        </Button>
      </View>
    </Modal>
  );
};

ModalExtras.propTypes = {
  busy: PropTypes.bool,
  cart: PropTypes.arrayOf(PropTypes.shape({})),
  extras: PropTypes.arrayOf(PropTypes.shape({})),
  taxesType: PropTypes.number,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

ModalExtras.displayName = 'Mirai:Core:Rates:ModalExtras';

export { ModalExtras };
